import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { ProductCard } from '../components/ProductCard';
import { ProductModal } from '../components/ProductModal';
import { VideoSection } from '../components/VideoSection';
import { toast } from 'sonner';
import { Truck, ChefHat, Flame, ArrowRight } from 'lucide-react';
import { trackPageview } from '../utils/tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://images.unsplash.com/photo-1767785990437-dfe1fe516fe8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMHByb2Zlc3Npb25hbCUyMGNvb2tpbmd8ZW58MHx8fHwxNzcxMjEwMzM0fDA&ixlib=rb-4.1.0&q=85";
const PRODUCT_LINEUP_DARK = "https://customer-assets.emergentagent.com/job_da8972b2-30fe-4905-bc28-1222202a53f5/artifacts/81k235wg_file_00000000a5fc71fdaa432c47144d4458.png";

export const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    trackPageview('home', '/');
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  const handleViewBlend = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !consent) {
      toast.error('Please enter your email and agree to receive updates');
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribe`, {
        email,
        source_page: 'homepage',
        consent,
      });
      toast.success('Welcome to the Bo crew! Check your inbox soon.');
      setEmail('');
      setConsent(false);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This email is already subscribed!');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleViewSeafood = () => {
    // Find the Seafood Seasoning product and open its modal
    const seafoodProduct = products.find(p => p.id === 'seafood-seasoning');
    if (seafoodProduct) {
      setSelectedProduct(seafoodProduct);
      setModalOpen(true);
    } else {
      // Fallback: scroll to product section if product not loaded yet
      const element = document.getElementById('seafood-seasoning');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Professional kitchen"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          {/* Product Lineup Image */}
          <div className="mb-8">
            <img
              src={PRODUCT_LINEUP_DARK}
              alt="Country Bo Seasonings Lineup"
              className="mx-auto max-w-[90%] sm:max-w-[600px] lg:max-w-[700px] h-auto drop-shadow-2xl"
              data-testid="hero-product-image"
            />
          </div>

          {/* Headline */}
          <h1 className="font-['Bodoni_Moda'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="hero-headline">
            Put Some Bo On It.
          </h1>
          <p className="text-lg sm:text-xl text-[#E5E0D8] mb-8 font-light" data-testid="hero-subhead">
            Big Flavor. No Fuss.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/shop">
              <Button
                size="lg"
                className="bg-[#D4540A] hover:bg-[#B84709] text-white px-8 py-6 text-lg font-semibold rounded-full transition-all hover:scale-105"
                data-testid="shop-blends-btn"
              >
                Shop the Blends
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              onClick={handleViewSeafood}
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-full"
              data-testid="view-seafood-btn"
            >
              View Seafood Seasoning
            </Button>
          </div>
        </div>
      </section>

      {/* Product Lineup Section */}
      <section className="py-16 sm:py-24 bg-[#FAF8F5]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Bodoni_Moda'] text-3xl sm:text-4xl font-bold text-[#2A2A2A] mb-4">
              The Lineup
            </h2>
            <p className="text-[#666] text-base sm:text-lg max-w-2xl mx-auto">
              Four bold blends. Each one designed to make your food taste better—fast.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.id} id={product.id}>
                <ProductCard product={product} onViewBlend={handleViewBlend} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Strip */}
      <section className="py-12 bg-[#2A2A2A]" data-testid="value-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div className="flex items-center justify-center sm:justify-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-[#D4540A] flex items-center justify-center flex-shrink-0">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Fast Shipping</h3>
                <p className="text-[#B8B5B0] text-sm">Quick delivery nationwide</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Made for Real Kitchens</h3>
                <p className="text-[#B8B5B0] text-sm">Pro-level flavor at home</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-[#5C3D2E] flex items-center justify-center flex-shrink-0">
                <Flame className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Bold Flavor, No Heat Focus</h3>
                <p className="text-[#B8B5B0] text-sm">All taste, your spice level</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section - See Country Bo In Action */}
      <VideoSection />

      {/* Email Capture Section */}
      <section className="py-16 sm:py-20 bg-[#F5F3F0]" data-testid="email-capture-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Bodoni_Moda'] text-3xl sm:text-4xl font-bold text-[#2A2A2A] mb-4">
            Get Recipes + Drops + Deals
          </h2>
          <p className="text-[#666] mb-8">
            Join the Bo crew for exclusive recipes, new product drops, and special offers.
          </p>

          <form onSubmit={handleSubscribe} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white border-[#E5E0D8] h-12 text-base"
                data-testid="email-input"
              />
              <Button
                type="submit"
                disabled={subscribing}
                className="bg-[#D4540A] hover:bg-[#B84709] text-white h-12 px-8 font-semibold"
                data-testid="subscribe-btn"
              >
                {subscribing ? 'Joining...' : 'Join the Crew'}
              </Button>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Checkbox
                id="consent"
                checked={consent}
                onCheckedChange={setConsent}
                data-testid="consent-checkbox"
              />
              <label htmlFor="consent" className="text-sm text-[#666] cursor-pointer">
                I agree to receive marketing emails from Country Bo Foods
              </label>
            </div>
          </form>
        </div>
      </section>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default HomePage;
